import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import MessageBanner from '../../../../ui/components/MessageBanner';
import Button from '../../../../ui/components/Button';
import ButtonMicro from '../../../../ui/components/ButtonMicro';
import Tooltip from '../../../../ui/components/Tooltip';
import MessageAlert from '../../../../ui/components/MessageAlert';
import GameStream from '../stream/components/GameStream';
import { useStreamUIState } from '../stream/helpers/streamStore';
import type { StreamContextType } from '../stream/helpers/Types';

// Example content for testing/development
const exampleContent = {
    "plan": [
        {
            "task": "Implement first-person controls: WASD for movement, mouse drag for 360° camera rotation, spacebar to jump (1-block height limit), and gravity simulation.  ",
            "status": "DONE",
            "friendly": "First-person movement and camera controls",
            "priority": 1
        },
        {
            "task": "Enable left-click block breaking (0.5-second hold with radial progress indicator) and right-click placement using selected inventory item, restricted to adjacent blocks.  ",
            "status": "PENDING",
            "friendly": "Block interaction with tools/selection",
            "priority": 1
        },
        {
            "task": "Generate a 3D grid-based world with procedurally generated terrain featuring grass, dirt, stone, and water blocks using a custom heightmap algorithm for elevation variation.  ",
            "status": "PENDING",
            // "status": "STALE",
            "friendly": "3D block world with terrain variety",
            "priority": 2
        },
        {
            "task": "Display a 9-slot hotbar at the bottom center with number keys 1-9 for selection, collected blocks stacking up to 64 per slot and showing quantity labels.  ",
            "status": "PENDING",
            "friendly": "Visible inventory with quick access",
            "priority": 2
        },
        {
            "task": "Create a day/night cycle with 5-minute day phases using smooth background color transitions (blue to black) and reduced block visibility at night.  ",
            "status": "PENDING",
            "friendly": "Day/night cycle with lighting changes",
            "priority": 2
        },
        {
            "task": "Add collision detection preventing movement through solid blocks, with falling damage (1 heart lost per block beyond 3-block drops) displayed via a top-left health bar.  ",
            "status": "PENDING",
            "friendly": "Block collision and fall damage",
            "priority": 2
        },
        {
            "task": "Include water physics allowing horizontal flow to adjacent empty spaces and simple particle effects when breaking blocks.  ",
            "status": "PENDING",
            "friendly": "Basic water flow mechanics",
            "priority": 2
        },
        {
            "task": "Generate trees procedurally with 4-block tall wood trunks and 2-layer leaf canopies on grass blocks at random intervals.",
            "status": "PENDING",
            "friendly": "Random tree generation system",
            "priority": 2
        },
        {
            "task": "Support mobile via touch: virtual joystick for movement, two-finger drag for camera control, and on-screen mine/place buttons with haptic feedback.  ",
            "status": "WAITING",
            "friendly": "Mobile touch controls added",
            "priority": 3
        },
        {
            "task": "Render block faces using textured WebGL canvases with optimized view distance (8 chunks loaded around player) and hidden surface removal.  ",
            "status": "WAITING",
            "friendly": "Optimized 3D rendering performance",
            "priority": 3
        }
    ],
    // "status": "Loading",
    // "status": "Initial Plan",
    "status": "Chose Initial Tasks",
    // "status": "Initial Testing 0",
    // "status": "Build For Test",
    // "status": "Initial Working Build",

    // "status": "Create Failed",
    // "status": "Edit Failed",

    "statusType": "First Creation",

    // "status": "Remove Background",
    // "status": "Auto-Generating Art",
    // "status": "Add Art to Game",
    // "statusType": "Add Game Art",
};

const userActionTypes = [
    "First Creation", 
    "Continue Building Plan",
    "Edit", 
    "Bugfix", 
    "Add Feature", 
];

interface MessageType {
    title: string;
    body?: string | string[];
    icon?: string;
    stages: string[];
    checklist?: {
        items: { id: string; text: string; checked: boolean; interactive?: boolean; }[];
        onItemChange?: (id: string, checked: boolean) => void;
    };
}

interface GameMessageProps {
    config: any;
    plan: string;
    gameId?: string;
    type?: 'progress' | 'suggestion';
    // hasStreamContent removed - will be reimplemented with SSE
    onAIStatusChange: (isRunning: boolean) => void;
    onSubmitSuggested?: (selectedItems: { id: string; text: string; checked: boolean; interactive?: boolean }[]) => void;
    onStreamContext?: (context: StreamContextType) => void;
}

const GameMessage: React.FC<GameMessageProps> = ({ config, plan, gameId, type = 'progress', onAIStatusChange, onSubmitSuggested, onStreamContext }) => {
    const [progressOpen, setProgressOpen] = useState(false);
    const [suggestionOpen, setSuggestionOpen] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [streamContext, setStreamContext] = useState<StreamContextType | null>(null);
    const [isStreamExpanded, setIsStreamExpanded] = useState(true);
    const { content, isStreaming, activeSessionId, turnColumnKeys, columnOrder } = useStreamUIState() as any;
    const hasStreamContent = content.blocks.length > 0;

    const messages: MessageType[] = [
        {
            title: 'Loading...', 
            icon: 'loading',
            stages: [
                undefined,
                null,
            ]
        },
        {
            title: 'Thinking...', 
            icon: 'loading',
            stages: [
                "Loading",
                "Thinking",
                "Initial Generation Begin", // NEW
                "Request Started", // NEW
                "Initial Plan",
                "Generating Plan",
                "Revision Attempt Enqueued",
            ]
        },
        {
            title: 'Building...', 
            icon: 'loading',
            stages: [
                "Chose Initial Tasks",
                "Revision Attempt",
                "Adding Feature",
                "Revision Attempt", // ${i}
                "Initial Add Attempt",
                "Add Attempt", // ${i}
            ]
        },
        {
            title: 'Verifying...', 
            icon: 'loading',
            stages: [
                "Initial Bugfix Attempt",
                "Initial Testing", // ${i}
                "Build For Test",
            ]
        },
        {
            title: 'Applying...', 
            icon: 'loading',
            stages: [
                // "Add Attempt Final",
            ]
        },
        {
            title: 'Removing background...', 
            icon: 'loading',
            stages: [
                "Remove Background",
            ]
        },
        {
            title: 'Auto-generating art...', 
            icon: 'loading',
            stages: [
                "Auto-Generating Art",
            ]
        },
        {
            title: 'Adding art to game...', 
            icon: 'loading',
            stages: [
                "Add Art to Game",
            ]
        },
        {
            title: 'DONE', 
            icon: 'check-slim',
            stages: [
                "Initial Working Build",
                "Second Round Testing Complete",
                "Add Complete",
                "Flow Complete",
                "Revision Complete",
                "Create Failed",
                "Edit Failed",
            ]
        },
    ];

    const errorMessages = [
        {
            stage: 'Create Failed',
            title: 'Uh oh! Glitch in the Game Matrix!',
            body: [
                'That game idea was super cool, but I struggled to build it all at once. Give game creation another shot!', 
                'HINT: Try describing just one core mechanic first, then add more detail after.'
            ],
        },
        {
            stage: 'Edit Failed',
            title: 'Oops! Edit Rewind!',
            body: [
                'I tried to make your change, but I couldn\'t quite figure it out. Let\'s try again?', 
                'TIP: Break edits down into small, bite-sized steps.'
            ],
        }
    ];

    const suggestionTitle = "Next Suggestions";
    const suggestionBody = null; // "Select up to 3 items to build next or write your own.";

    const [currentMessage, setCurrentMessage] = useState<MessageType>(messages[0]);
    const [hasCheckedItems, setHasCheckedItems] = useState(false);
    const [hasSuggestionItems, setHasSuggestionItems] = useState(false);
    const [statusPhase, setStatusPhase] = useState<string>('');
    const lastPlanStatusRef = useRef<string | null>(null);
    const lastPlanStatusChangeAtRef = useRef<number>(0);
    const [serverOnlyMode, setServerOnlyMode] = useState(false);

    // // Debug: log when isStreaming flips
    // useEffect(() => {
    //     try { console.log('[GameMessage] isStreaming changed:', isStreaming); } catch (_) {}
    // }, [isStreaming]);

    // // Debug: log when progressOpen flips
    // useEffect(() => {
    //     try { console.log('[GameMessage] progressOpen:', progressOpen); } catch (_) {}
    // }, [progressOpen]);

    useEffect(() => {
        setMessageBasedOnStatus();
    }, []);

    useEffect(() => {
        setMessageBasedOnStatus();
    }, [plan]);

    // Re-evaluate visibility when streaming state changes to promptly close/open tooltip
    useEffect(() => {
        setMessageBasedOnStatus();
    }, [isStreaming]);

    // Check if any items are checked
    useEffect(() => {
        if (type === 'suggestion') {
            if (currentMessage.checklist && currentMessage.checklist.items) {
                const anyChecked = currentMessage.checklist.items.some(item => item.checked);
                setHasCheckedItems(anyChecked);
            } else {
                setHasCheckedItems(false);
            }
        }
    }, [currentMessage.checklist, type]);

    const setMessageBasedOnStatus = () => {
        let parsedPlan: any = {};
        try { parsedPlan = JSON.parse(plan); } catch (_) { parsedPlan = {}; }
        if (config.DEBUG_PLAN) {
            parsedPlan = exampleContent;
        }

        // Find plan-based message (primary driver)
        const matchingMessage = parsedPlan?.status
            ? messages.find(message => message.stages.some(stage => parsedPlan.status?.includes?.(stage)))
            : undefined;

        const isDone = matchingMessage && matchingMessage.title === messages[messages.length - 1].title;
        const isPlanInProgress = !!matchingMessage && !isDone;

        // Track freshness of plan status transitions
        const statusStr: string | null = parsedPlan?.status || null;
        if (statusStr !== lastPlanStatusRef.current) {
            lastPlanStatusRef.current = statusStr;
            lastPlanStatusChangeAtRef.current = Date.now();
        }
        const planFreshMs = Date.now() - (lastPlanStatusChangeAtRef.current || 0);
        const isPlanFresh = planFreshMs < 5000; // 5s freshness window (not used for keeping open)

        // Server-only recovery mode: stream not connected but plan indicates in-progress
        const serverOnlyActive = (isStreaming !== true) && isPlanInProgress;
        setServerOnlyMode(serverOnlyActive);

        // Decide visibility: open when DB says in-progress or session active (do not allow DB to close while session exists)
        const shouldOpen = (type === 'progress')
            ? (isDone
                ? (isStreaming === true)
                : ((isPlanInProgress && (isStreaming === true || serverOnlyActive)) || isStreaming === true))
            : (suggestionOpen);

        // Debug: log evaluation details
        // try {
        //     console.log('[GameMessage] evaluate', {
        //         status: parsedPlan?.status || null,
        //         matchingMessage: matchingMessage?.title,
        //         isStreaming,
        //         isPlanInProgress,
        //         isDone,
        //         serverOnlyActive,
        //         shouldOpen,
        //         type,
        //     });
        // } catch (_) {}

        setProgressOpen(shouldOpen);
        onAIStatusChange(shouldOpen);

        if (shouldOpen) {
            // Prefer plan-derived message when in-progress or server only mode; otherwise fallback to 'Thinking…' 
            if (serverOnlyActive || isPlanInProgress) {
                setCurrentMessage(matchingMessage || messages[1]);
            } else {
                setCurrentMessage(messages[1]); // 'Thinking…'
            }
        } else if (matchingMessage) {
            // Keep current message in sync even when closed (e.g., DONE)
            setCurrentMessage(matchingMessage);
        }

        // Preserve existing checklist behavior for plan-first messages
        if (parsedPlan?.status && matchingMessage && type === 'progress' && matchingMessage.title !== messages[0].title) {
            const isStale = parsedPlan.plan?.some?.((x: any) => x.status === 'STALE');
            if (!isStale && parsedPlan.statusType === 'First Creation') {
                if (parsedPlan.plan && Array.isArray(parsedPlan.plan)) {
                    const checklistItems = parsedPlan.plan
                        .filter((item: any) => item.priority === 1 && item.friendly && item.friendly.trim() !== '')
                        .map((item: any, index: number) => ({ id: index.toString(), text: item.friendly.trim(), checked: item.status === 'DONE', interactive: false }));
                    if (checklistItems.length > 0) {
                        setCurrentMessage(prev => ({
                            ...prev,
                            checklist: { items: checklistItems, onItemChange: () => {}, showLoading: true }
                        }));
                    }
                }
            }
        }

        // Suggestion logic unchanged and still gated on DONE state
        if (parsedPlan?.status && type === 'suggestion' && matchingMessage && matchingMessage.title === messages[messages.length - 1].title) {
            const isStale = parsedPlan.plan?.some?.((x: any) => x.status === 'STALE');
            if (parsedPlan.status === 'Create Failed') {
                setCurrentMessage({ ...matchingMessage, icon: 'exclamation', title: errorMessages[0].title, body: errorMessages[0].body });
                setErrorOpen(true);
            } else if (parsedPlan.status === 'Edit Failed') {
                setCurrentMessage({ ...matchingMessage, icon: 'exclamation', title: errorMessages[1].title, body: errorMessages[1].body });
                setErrorOpen(true);
            } else if (!isStale && config.GAME_DETAILED_MESSAGES && parsedPlan.statusType === 'First Creation') {
                if (parsedPlan.plan && Array.isArray(parsedPlan.plan)) {
                    const suggestionItems = parsedPlan.plan
                        .filter((item: any) => item.priority === 2 && item.status !== 'DONE' && item.friendly && item.friendly.trim() !== '')
                        .slice(0, 3)
                        .map((item: any, index: number) => ({ id: index.toString(), text: item.friendly.trim(), rawText: item.task, interactive: true }));
                    if (suggestionItems.length > 0) {
                        setHasSuggestionItems(true);
                        setCurrentMessage({ title: suggestionTitle, body: suggestionBody, icon: 'lightbulb', stages: matchingMessage.stages, checklist: { items: suggestionItems, onItemChange: (id, checked) => {
                            setCurrentMessage(prev => {
                                if (!prev.checklist) return prev;
                                const updatedItems = prev.checklist.items.map(item => item.id === id ? { ...item, checked } : item);
                                return { ...prev, checklist: { ...prev.checklist, items: updatedItems } };
                            });
                        } } });
                        setSuggestionOpen(true);
                    }
                }
            }
        }
    };

    // useEffect(() => {
    //     if (type === 'suggestion') {
    //         console.log('current message', currentMessage);
    //         console.log('suggestionOpen', suggestionOpen);
    //     }
    // }, [type, currentMessage, suggestionOpen]);

    // useEffect(() => {
    //     console.log('suggestionOpen', suggestionOpen);
    // }, [suggestionOpen]);

    // useEffect(() => {
    //     console.log('statusPhase', statusPhase);
    // }, [statusPhase]);

    // Handle stream context
    const handleStreamContext = useCallback((context: StreamContextType) => {
        setStreamContext(context);
        onStreamContext?.(context);
    }, [onStreamContext]);

    // Determine body and checklist content based on conditions
    const isStreamingMessage = config.ENABLE_AI_STREAM && (
        currentMessage.title !== 'Thinking...' && currentMessage.title !== 'DONE'
    );
    // Show detailed stream UI only while there is an active client session
    const shouldShowStreamUI = isStreamingMessage && hasStreamContent && !serverOnlyMode && !!activeSessionId;
    const bannerBodyText = (isStreamingMessage && !serverOnlyMode) ? null : currentMessage.body;
    const bannerChecklist = (isStreamingMessage && !serverOnlyMode) ? null : currentMessage.checklist;

    // Determine if stream should be visible
    const shouldShowStream = (isStreamingMessage || hasStreamContent) && !serverOnlyMode && (!!activeSessionId);

    // Determine icon: hide loading icon when AI stream is enabled
    const displayIcon = currentMessage.icon; // (config.ENABLE_AI_STREAM && currentMessage.icon === 'loading') ? undefined : currentMessage.icon;

    // Create single GameStream instance to avoid losing content when switching between contexts
    const streamViewElement = (config.ENABLE_AI_STREAM && shouldShowStream && isStreamExpanded) ? (
        <GameStream isExpanded={true} />
    ) : null;

    // Detect active parallel sub-agents using stream state
    const hasParallelSubAgents = Array.isArray(turnColumnKeys)
        ? (turnColumnKeys.length > 1)
        : (Array.isArray(columnOrder) && columnOrder.length > 1);

    return (
        <>
            {/* StreamStateProvider mounted at page level (GameEdit) to avoid duplicates */}

            <MessageBanner
                colorMode="dark"
                icon={displayIcon}
                titleText={currentMessage.title}
                bodyText={bannerBodyText}
                checklist={bannerChecklist}
                open={type === 'progress' ? progressOpen : suggestionOpen}
                includeCloseButton={type === 'suggestion' ? true : null}
                onClose={() => type === 'progress' ? setProgressOpen(false) : setSuggestionOpen(false)}
                primaryButton={type === 'suggestion' ? {
                    // text: 'Create',
                    icon: 'send',
                    disabled: !hasCheckedItems, // Disable button if no items are checked
                    onClick: () => {
                        const selectedItems = currentMessage.checklist?.items.filter(item => item.checked) || [];
                        // console.log('Create button clicked with selected items:', selectedItems);
                        onSubmitSuggested?.(selectedItems);
                        setSuggestionOpen(false);
                    }
                } : undefined}
                headerAction={config.ENABLE_AI_STREAM && hasStreamContent && !serverOnlyMode ? (
                    <ButtonMicro 
                        colorMode="dark" 
                        padding="4px"
                        showBorder={false}
                        icon={isStreamExpanded ? 'up' : 'down'} 
                        onClick={() => setIsStreamExpanded(!isStreamExpanded)} 
                    />
                ) : undefined}
                
                // anchorOrigin={{
                //     vertical: 'top',
                //     horizontal: 'center',
                // }}
                    
                anchorOrigin={type === 'progress' ? {
                    vertical: 'top',
                    horizontal: 'center',
                } : null}
                manualPosition={type === 'suggestion' ? true : null}
                style={type === 'suggestion' ? {
                    position: 'fixed',
                    bottom: '61px',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    zIndex: 1000,
                } : null}
                minWidth={type === 'suggestion' ? '240px' : (type === 'progress' && hasParallelSubAgents ? '60vw' : null)}
                maxWidth="calc(100vw - 32px)"
            >
                {streamViewElement}
            </MessageBanner>

            {type === 'suggestion' && (
                <ErrorContainer>
                    {/* <MessageAlert 
                        titleText={errorMessages[0].title}
                        bodyText={errorMessages[0].body}
                        color='red'
                        includeClose={true}
                    /> */}
                    <MessageBanner
                        colorMode="dark"
                        icon={displayIcon}
                        titleText={currentMessage.title}
                        bodyText={currentMessage.body}
                        open={errorOpen}
                        includeCloseButton={true}
                        onClose={() => setErrorOpen(false)}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        maxWidth="calc(100vw - 32px)"
                    />
                </ErrorContainer>
            )}
            {type === 'suggestion' && !suggestionOpen && hasSuggestionItems && statusPhase === 'First Creation' && (
                <SuggestionContainer>
                    <Tooltip colorMode='dark' text='Suggestions' position='top' >
                        <ButtonMicro 
                            icon='up' 
                            onClick={() => setSuggestionOpen(true)} 
                            colorMode='dark'
                            size={16} 
                            padding='0px 4px' 
                            borderRadius={'4px 4px 0 0'}
                        />
                    </Tooltip>
                </SuggestionContainer>
            )}
        </>
    );
};

export default GameMessage;


const SuggestionContainer = styled.div`
    position: fixed;
    bottom: 61px;
    left: 50%;
    transform: translate(-50%, 0);
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 1000;
`;

const ErrorContainer = styled.div`
    position: absolute;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 100%;
    max-width: 100vw;
    z-index: 1000;
`;